import { FeatureFlag } from '@/constants/feature-flags.ts';
import { AppointmentBookStatus } from '@/models/appointment.ts';
import { IconButton, useMediaQuery } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import isStrongPassword from 'validator/es/lib/isStrongPassword';
import { useShallow } from 'zustand/react/shallow';

import closeIcon from '@/assets/Icons/ic_close_black.svg';
import locationIcon from '@/assets/Icons/location.png';

import { Context as AuthContext } from '@/context/AuthContext.js';
import { Context as BookAppointmentContext } from '@/context/BookAppointmentContext.js';
import { ContextProvider } from '@/context/ProjectContext.jsx';

import { useFeatureFlag } from '@/hooks/use-feature-flag.ts';
import { useStartPasswordReset } from '@/hooks/use-start-password-reset.js';
import useI18nCountries from '@/hooks/useI18nCountries.js';

import { adressApi } from '@/service/AnamnesisSectionAPI/PersonalnformationAPI.js';
import { bookCollection } from '@/service/Appointments/book-collection-service.ts';

import { PVSProvider, isTimeSlotInvalid } from '@/utils/availabilityUtils.js';
import { resolveBookedChainMainPractitionerProfile } from '@/utils/chain-main-practitioner-utils.ts';
import { BookingStep, DEFAULT_COUNTRY, HealthInsurance } from '@/utils/constants.js';
import { isEmail, isMobilePhoneNumber } from '@/utils/formUtils.js';
import { JourneyType, bookAppointment, getText } from '@/utils/helpers.js';
import { isValidPostalCode } from '@/utils/localeUtils.js';
import {
    SIGN_UP_CONFIRMED_WITH_OTHER_PASSWORD,
    checkEmailExists,
    confirmCognitoSignUp,
    resumeSignUp,
    signUpWithCognito,
} from '@/utils/registerUtils.js';

import {
    CLEARED_CHAIN_PAGING,
    useBookingStore,
    useConfigStore,
    usePersistedPrismicStore,
    useSearchStore,
} from '@/store.js';

import Address from './BookingModal/Address.jsx';
import { AuthSelection } from './BookingModal/AuthSelection.tsx';
import ConfirmAppointment from './BookingModal/ConfirmAppointment.jsx';
import Consent from './BookingModal/Consent.jsx';
import InvalidTimeslot from './BookingModal/InvalidTimeslot.jsx';
import Registration from './BookingModal/Registration.jsx';
import TimeslotCalendar from './BookingModal/TimeslotCalendar.jsx';
import VerificationCode from './BookingModal/VerificationCode.jsx';
import {
    isChainContinueDisabled,
    resolveAuthBackTarget,
    resolveAuthFlowTarget,
} from './BookingModal/booking-step-routing.ts';
import ChainSuggestionsStep from './BookingModal/chain-suggestions-step.tsx';
import LoginDialogBox from './Common/LoginDialogBox.jsx';
import FilledButton from './FilledButton.jsx';
import UnderlinedButton from './UnderlinedButton.jsx';

const useStyles = makeStyles((theme) => ({
    container: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'start',
        paddingTop: '10vh',
        justifyContent: 'center',
        zIndex: theme.zIndex.modal,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(0.1rem)',
        [theme.breakpoints.down('xs')]: {
            paddingTop: '0',
        },
    },
    hiddenBehindLogin: {
        visibility: 'hidden',
    },
    modalWrapper: {
        position: 'relative',
        width: '50rem',
        maxHeight: '80vh',
        borderRadius: theme.radius.default,
        overflow: 'hidden',

        [theme.breakpoints.down('sm')]: {
            width: '70%',
        },
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            height: '100%',
            maxHeight: '100vh',
            borderRadius: 0,
        },
    },
    modal: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxHeight: '80vh',
        background: theme.palette.common.white,
        borderRadius: theme.radius.default,
        boxSizing: 'border-box',
        userSelect: 'none',
        overflow: 'hidden',
        [theme.breakpoints.down('xs')]: {
            height: '100%',
            maxHeight: '100vh',
            borderRadius: 0,
            position: 'relative',
        },
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        padding: '1rem 2.5rem',
        scrollbarGutter: 'stable',
        '&::-webkit-scrollbar': {
            width: '0.5rem',
        },
        '&::-webkit-scrollbar-track': {
            background: theme.palette.common.inputDisabledGray,
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.common.midGrey,
            borderRadius: '0.25rem',
        },
        // Firefox has no ::-webkit-scrollbar. Scoping the standard properties to it keeps Chrome on the
        // rules above, which it would otherwise discard in favour of its own overlay scrollbar.
        '@supports not selector(::-webkit-scrollbar)': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.common.midGrey} ${theme.palette.common.inputDisabledGray}`,
        },
        [theme.breakpoints.down('xs')]: {
            padding: '1rem 2rem',
        },
    },
    header: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '1rem 2.5rem 0.75rem 2.5rem',
        background: theme.palette.common.white,
        borderBottom: theme.border.light,
        [theme.breakpoints.down('xs')]: {
            padding: '1rem 2rem 0.75rem 2rem',
        },
    },
    headerClose: {
        flexShrink: 0,
        cursor: 'pointer',
        padding: '0.5rem',
        '& img': {
            width: '1.25rem',
            height: '1.25rem',
        },
    },
    stepTitle: {
        fontWeight: 400,
        fontSize: '1.25rem',
    },
    stepSubtitle: {
        ...theme.typography.body1,
        color: theme.palette.common.darkGrey,
        opacity: 0.7,
        marginTop: '-0.25rem',
    },
    stepSubtitleLink: {
        color: 'inherit',
        textDecoration: 'underline',
    },
    navBar: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        padding: '1.25rem 3rem 2.5rem 2.5rem',
        background: theme.palette.common.white,
        borderTop: theme.border.light,

        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column-reverse',
            gap: '1rem',
            padding: '1.25rem 2rem 2rem 2rem',
        },
    },
}));

const StepComponent = {
    [BookingStep.TIMESLOT_SELECTION]: TimeslotCalendar,
    [BookingStep.AUTH_SELECTION]: AuthSelection,
    [BookingStep.REGISTRATION]: Registration,
    [BookingStep.ADDRESS]: Address,
    [BookingStep.VERIFICATION_CODE]: VerificationCode,
    [BookingStep.CONFIRM_APPOINTMENT]: ConfirmAppointment,
    [BookingStep.CONSENT]: Consent,
    [BookingStep.INVALID_TIMESLOT]: InvalidTimeslot,
    [BookingStep.CHAIN_SUGGESTIONS]: ChainSuggestionsStep,
};

const BookingModal = () => {
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('xs'));
    const countryList = useI18nCountries();
    const startPasswordReset = useStartPasswordReset();
    const {
        state: { user, userAddresses, usersession },
        setuser,
        setuseraccounts,
        setusersession,
        setUserAddress,
    } = useContext(AuthContext);
    const { setConfirmDoctor } = useContext(BookAppointmentContext);
    const { setAppointmentSearchConfirmation, setAppointmentSelectionConfirmation, setAppointmentSearch } =
        useContext(ContextProvider);
    const classes = useStyles();
    const [consentToTreatments, setConsentToTreatments] = useState(false);
    const {
        treatments,
        locations,
        selectedTreatments,
        selectedLocation,
        selectedHealthInsurance,
        selectedChainConfig,
        chainAvailability,
        chainIsReturningPatient,
        chainHasSuggestions,
    } = useSearchStore(
        useShallow((state) => ({
            treatments: state.treatments,
            locations: state.locations,
            selectedTreatments: state.selectedTreatments,
            selectedLocation: state.selectedLocation,
            selectedHealthInsurance: state.selectedHealthInsurance,
            selectedChainConfig: state.selectedChainConfig,
            chainAvailability: state.chainAvailability,
            chainIsReturningPatient: state.chainIsReturningPatient,
            chainHasSuggestions: state.chainAvailability.length > 0,
        }))
    );
    const isChainFlow = Boolean(selectedChainConfig);
    const { value: isChainBookingEnabled } = useFeatureFlag(FeatureFlag.IS_CHAIN_BOOKING_ENABLED_FOR_PATIENTS);
    const isChainOverview = isChainFlow && Boolean(isChainBookingEnabled) && chainHasSuggestions;
    const {
        selectedProfessional,
        professionalDetails,
        currentStep,
        isReturningPatient,
        selectedTimeslot,
        loading,
        appointmentData,
        additionalInfo,
        signUpStarted,
        showLoginModal,
        isOnlineAppointment,
        consentTreatments,
        isProfessionalSelectionSkipped,
        pvsProvider,
        isChainSuggestionFetching,
        registrationFormData: {
            selectedGender,
            firstName,
            lastName,
            dob,
            email,
            phoneNumber,
            confirmPassword,
            password,
            street,
            houseNumber,
            zipCode,
            city,
            country,
            verificationCode,
            isConsentConfirmed,
            isAnalyticsConfirmed,
        },
    } = useBookingStore();

    const {
        [i18n.language]: { mainData: bookAppointmentPrismicData },
    } = usePersistedPrismicStore((state) => state.bookAppointmentData);
    const {
        [i18n.language]: { content: accountPrismicData },
    } = usePersistedPrismicStore((state) => state.accountData);
    const {
        [i18n.language]: { benefits: registerPrismicData },
    } = usePersistedPrismicStore((state) => state.registerData);
    const modalRef = useRef();

    const registrationSteps = [BookingStep.REGISTRATION, BookingStep.ADDRESS, BookingStep.VERIFICATION_CODE];
    const showProgressBar = registrationSteps.includes(currentStep);

    // The chosen availability is held across the auth steps, so post-auth landing routes straight to the
    // overview — no re-fetch, no clearing of the held selection.
    const goToChainOverview = () => {
        useBookingStore.setState({
            currentStep: BookingStep.CONFIRM_APPOINTMENT,
            loading: false,
            isChainSuggestionFetching: false,
        });
    };

    const updateAddress = async () => {
        useBookingStore.setState({ loading: true });
        try {
            const safeCountry = country || DEFAULT_COUNTRY;
            const addressData = {
                user_id: user.id,
                street: street,
                house_number: houseNumber,
                zip: zipCode,
                city: city,
                country: {
                    code: safeCountry,
                    name: countryList[safeCountry.toUpperCase()],
                },
            };

            await adressApi(usersession, addressData);
            setUserAddress(addressData);

            if (isChainFlow) {
                goToChainOverview();
                return;
            }

            useBookingStore.setState({
                loading: false,
                currentStep: BookingStep.CONFIRM_APPOINTMENT,
            });
        } catch (e) {
            console.debug(e);
            toast.error(t('AddressSaveFailed'));
            useBookingStore.setState({ loading: false });
        }
    };

    // The email belongs to an earlier sign-up that was never confirmed: send a new code and pick it up at the code
    // step. A confirmed account can't be resumed, so the patient is told the email is already in use.
    const continueUnfinishedSignUp = async () => {
        if (await resumeSignUp(email)) {
            useBookingStore.setState({
                currentStep: BookingStep.VERIFICATION_CODE,
                loading: false,
                signUpStarted: true,
            });
            return;
        }
        useBookingStore.getState().setEmailCheck(true, email);
        useBookingStore.setState({ currentStep: BookingStep.REGISTRATION, loading: false });
    };

    const signUpUser = async () => {
        try {
            useBookingStore.setState({ loading: true });

            await signUpWithCognito(
                {
                    email: email,
                    password: confirmPassword,
                    firstName: firstName,
                    lastName: lastName,
                    selectedGender: selectedGender,
                    dob: dob,
                    phoneNumber: phoneNumber,
                    language: i18n.language,
                },
                registerPrismicData.gender_values
            );

            useBookingStore.setState({
                currentStep: BookingStep.VERIFICATION_CODE,
                loading: false,
                signUpStarted: true,
            });
        } catch (e) {
            console.debug(e);
            if (e?.code === 'UsernameExistsException') {
                await continueUnfinishedSignUp();
                return;
            }
            useBookingStore.setState({ loading: false });
        }
    };

    const confirmSignUp = async () => {
        useBookingStore.setState({ loading: true });
        try {
            const { result: response, signInUser } = await confirmCognitoSignUp(
                {
                    email: email,
                    verificationCode: verificationCode,
                    password: confirmPassword,
                    firstName: firstName,
                    lastName: lastName,
                    selectedGender: selectedGender,
                    dob: dob,
                    phoneNumber: phoneNumber,
                    street: street,
                    houseNumber: houseNumber,
                    zipCode: zipCode,
                    city: city,
                    country: country,
                    language: i18n.language,
                    isAnalyticsConfirmed: isAnalyticsConfirmed,
                },
                registerPrismicData.gender_values,
                countryList,
                selectedHealthInsurance.toUpperCase().replace('_', '')
            );

            if (!response) {
                useBookingStore.setState({ loading: false });
                return;
            }

            setuseraccounts(response.account);
            setuser(response.user);
            setusersession(signInUser.signInUserSession);
            setUserAddress(response.address);

            useBookingStore.getState().setVerificationCodeCheck(false, '');

            if (isChainFlow) {
                useBookingStore.setState({ signUpStarted: false });
                goToChainOverview();
                return;
            }

            useBookingStore.setState({
                currentStep: BookingStep.CONFIRM_APPOINTMENT,
                loading: false,
                signUpStarted: false,
            });
        } catch (error) {
            if (error.code === 'CodeMismatchException') {
                useBookingStore.getState().setVerificationCodeCheck(true, verificationCode);
            }
            if (error.code === SIGN_UP_CONFIRMED_WITH_OTHER_PASSWORD) {
                useBookingStore.setState({ loading: false, signUpStarted: false, showBookingModal: false });
                await startPasswordReset(email);
                return;
            }
            useBookingStore.setState({ loading: false });
        }
    };

    const buildBookAppointmentDataObj = () => {
        const patientObj = {
            id: user.id,
            is_for_self: true,
            is_returning: isReturningPatient,
            is_public_insurance: selectedHealthInsurance === HealthInsurance.PUBLIC,
            is_private_insurance: selectedHealthInsurance === HealthInsurance.PRIVATE,
            is_self_payer: selectedHealthInsurance === HealthInsurance.SELF_PAYER,
            documents: [],
            symptoms: [],
            is_with_prescription: false,
        };

        const professionalObj = {
            id: professionalDetails.id,
            key: selectedProfessional.key[0].text,
            type_key: selectedProfessional.specialityType.key[0].text,
            location_key: selectedLocation,
        };

        const appointmentObj = {
            id: appointmentData.id,
            types: appointmentData.type_keys.map((typeKey) => ({
                key: typeKey,
                name: treatments[selectedLocation]?.[typeKey].name[0].text,
                is_mapped: true,
            })),
            slot: selectedTimeslot,
            duration: appointmentData.duration,
            is_eterno_user: true,
            is_at_location: appointmentData.is_at_location,
            is_online: appointmentData.is_online,
            info: additionalInfo,
        };

        const pvsObj = {
            pvs_provider: pvsProvider,
        };

        if (pvsProvider === PVSProvider.DOC_CIRRUS) {
            pvsObj.doc_cirrus = {
                professional_id: appointmentData.meta.user_id,
                schedule_type_id: appointmentData.meta.type_id,
                calendar_id: appointmentData.meta.calendar_id,
            };
        } else if (pvsProvider === PVSProvider.DATA_CUR) {
            pvsObj.data_cur = {
                professional_id: appointmentData.meta.user_id,
                event_type_id: appointmentData.meta.type_id,
                resource_id: appointmentData.meta.resource_id,
                event_type_code: appointmentData.meta.event_type_code,
            };
        }

        return {
            patient: patientObj,
            professional: professionalObj,
            appointment: appointmentObj,
            pvs: pvsObj,
        };
    };

    const processErrorMessage = (errorMessage) => {
        const isSlotInvalid = isTimeSlotInvalid(errorMessage);

        if (isSlotInvalid) {
            useBookingStore.setState({
                loading: false,
                selectedTimeslot: null,
                invalidTimeSlot: selectedTimeslot,
                currentStep: BookingStep.INVALID_TIMESLOT,
            });
            return;
        }

        useBookingStore.setState({ loading: false });
        toast.error(t('AppointmentBookingFailed'));
    };

    const book = async () => {
        useBookingStore.setState({ loading: true });
        const bookAppointmentData = buildBookAppointmentDataObj();

        const [isBooked, bookedAppointment, errorMessage] = await bookAppointment(
            user.id,
            bookAppointmentData,
            usersession.idToken.jwtToken
        );

        if (!isBooked) {
            processErrorMessage(errorMessage);
            return;
        }

        useBookingStore.setState({ loading: false });

        const {
            [i18n.language]: { professionalProfiles },
        } = usePersistedPrismicStore.getState().findAppointmentData;
        setConfirmDoctor({
            ...professionalProfiles.find(
                (professional) => selectedProfessional.key[0].text === professional.key[0].text
            ),
            id: professionalDetails.id,
        });

        const {
            [i18n.language]: { content: findAppointmentPrismicData },
        } = usePersistedPrismicStore.getState().findAppointmentData;
        setAppointmentSearchConfirmation({
            location_key: selectedLocation,
            professional_type_key: selectedProfessional.specialityType.key[0].text,
            insurance_type_key: selectedHealthInsurance,
            professional_name: selectedProfessional.display_name[0].text,
            is_for_self: true,
            is_with_prescription: false,
            is_haveImg: false,
            symptoms: [],
            other_symptom: '',
            map_title: findAppointmentPrismicData[selectedLocation]
                ? findAppointmentPrismicData[selectedLocation].title
                : 'Online',
            location_title: findAppointmentPrismicData[selectedLocation]?.eterno_map_title ?? 'Online',
            journey_type: JourneyType.SEARCH_BOOK,
        });
        setAppointmentSelectionConfirmation({
            selected_appointment: selectedTimeslot,
        });
        setAppointmentSearch({
            insurance_type_key: selectedHealthInsurance,
            treatment_keys_array: appointmentData.type_keys,
        });
        useBookingStore.setState({ bookedAppointmentData: bookedAppointment });

        document.body.style.overflow = 'unset';

        history.push('/confirm-appointment');
    };

    const clearBookingStore = () => {
        const { bookedCollectionId, chainBookingFailed, isChainConfirmation, resetStore } = useBookingStore.getState();
        resetStore();
        useBookingStore.setState({ bookedCollectionId, chainBookingFailed, isChainConfirmation });
    };

    // Books the held chain collection from the overview CTA; any failure keeps the patient on the
    // overview with a single error toast.
    const bookChain = async () => {
        useBookingStore.setState({ loading: true });
        try {
            const customerId = useConfigStore.getState().currentCustomer?.customer_id?.[0]?.text;
            if (!customerId) {
                throw new Error('chain-book: missing customer_id');
            }
            const data = await bookCollection(usersession, user.id, customerId, {
                locationKey: selectedLocation,
                chainConfigurationId: selectedChainConfig.id,
                insurance: selectedHealthInsurance,
                isReturningPatient: chainIsReturningPatient === true,
                additionalInfo: additionalInfo,
                availability: chainAvailability,
            });

            const {
                [i18n.language]: { professionalProfiles },
            } = usePersistedPrismicStore.getState().findAppointmentData;
            const practiceName = getText(locations?.[selectedLocation]?.title) ?? '';
            setConfirmDoctor(
                resolveBookedChainMainPractitionerProfile({
                    appointments: data.pre_booked_appointments,
                    chainConfiguration: selectedChainConfig,
                    professionalProfiles: professionalProfiles,
                    practiceName: practiceName,
                    practiceAddress: getText(locations?.[selectedLocation]?.address),
                    fallbackImageUrl: locationIcon,
                })
            );
            setAppointmentSearchConfirmation({
                location_key: selectedLocation,
                location_title: practiceName || 'Online',
                is_returning: chainIsReturningPatient,
                journey_type: JourneyType.SEARCH_BOOK,
            });

            const chainBookingFailed =
                data.pre_booked_appointments.length === 0 ||
                data.pre_booked_appointments.some((appointment) => appointment.status !== AppointmentBookStatus.BOOKED);

            useBookingStore.setState({
                bookedCollectionId: data.collection.id,
                chainBookingFailed: chainBookingFailed,
                isChainConfirmation: true,
                loading: false,
            });

            document.body.style.overflow = 'unset';
            clearBookingStore();
            history.push('/confirm-appointment');
        } catch (error) {
            console.error(error);
            useBookingStore.setState({ loading: false });
            toast.error(t('ChainBookingError'));
        }
    };

    // Auth routing shared by TIMESLOT_SELECTION and CHAIN_SUGGESTIONS.
    const goToAuthFlow = () => {
        useBookingStore.setState({
            currentStep: resolveAuthFlowTarget({
                user: Boolean(user),
                userAddresses: Boolean(userAddresses),
                signUpStarted: signUpStarted,
            }),
        });
    };

    const nextStep = async () => {
        switch (currentStep) {
            case BookingStep.TIMESLOT_SELECTION:
                goToAuthFlow();
                break;
            case BookingStep.CHAIN_SUGGESTIONS:
                // Authed+addressed chain patients resolve straight to the overview via resolveAuthFlowTarget.
                goToAuthFlow();
                break;
            case BookingStep.REGISTRATION: {
                useBookingStore.setState({ loading: true });
                const isEmailInUse = await checkEmailExists(email);
                if (isEmailInUse) {
                    useBookingStore.getState().setEmailCheck(true, email);
                    useBookingStore.setState({ loading: false });
                    return;
                }
                useBookingStore.getState().setEmailCheck(false, '');
                useBookingStore.setState({ currentStep: BookingStep.ADDRESS, loading: false });
                break;
            }
            case BookingStep.CONSENT:
                useBookingStore.setState({ currentStep: BookingStep.TIMESLOT_SELECTION });
                break;
            case BookingStep.ADDRESS:
                if (user) await updateAddress();
                else await signUpUser();
                break;
            case BookingStep.VERIFICATION_CODE:
                await confirmSignUp();
                break;
            case BookingStep.CONFIRM_APPOINTMENT:
                if (isChainOverview) {
                    await bookChain();
                    break;
                }
                await book();
                break;
            default:
                break;
        }
    };

    const previousStep = () => {
        switch (currentStep) {
            case BookingStep.CONSENT:
                useBookingStore.setState({ showBookingModal: false });
                break;
            case BookingStep.CHAIN_SUGGESTIONS:
                // The step no longer owns Back, so clear its paging stack here before closing.
                useSearchStore.setState({ ...CLEARED_CHAIN_PAGING });
                useBookingStore.setState({ showBookingModal: false });
                break;
            case BookingStep.TIMESLOT_SELECTION: {
                // this is used in timeslot in order to reopen patientTypeSelection when user press back
                if (isProfessionalSelectionSkipped) {
                    useBookingStore.setState({ showPatientTypeSelection: true });
                }

                if (consentTreatments.length === 0) {
                    useBookingStore.setState({ showBookingModal: false });
                } else {
                    useBookingStore.setState({ currentStep: BookingStep.CONSENT });
                }
                break;
            }
            case BookingStep.AUTH_SELECTION:
                // Keep the chain suggestions intact so backing out lands on the same page (no refetch).
                useBookingStore.setState({ currentStep: resolveAuthBackTarget(isChainFlow) });
                break;
            case BookingStep.REGISTRATION:
                useBookingStore.setState({ currentStep: BookingStep.AUTH_SELECTION });
                break;
            case BookingStep.ADDRESS:
                useBookingStore.setState({ currentStep: BookingStep.REGISTRATION });
                break;
            case BookingStep.VERIFICATION_CODE:
                useBookingStore.setState({ currentStep: resolveAuthBackTarget(isChainFlow) });
                break;
            case BookingStep.CONFIRM_APPOINTMENT:
                useBookingStore.setState({ currentStep: resolveAuthBackTarget(isChainFlow) });
                break;
            default:
                break;
        }
    };

    const getStepTitle = () => {
        switch (currentStep) {
            case BookingStep.TIMESLOT_SELECTION:
                return bookAppointmentPrismicData.select_time_slot[0].text;
            case BookingStep.CONSENT:
                return bookAppointmentPrismicData.consent_step_title[0].text;
            case BookingStep.AUTH_SELECTION:
                return t('AuthSelectionTitle');
            case BookingStep.REGISTRATION:
                return registerPrismicData.step_title;
            case BookingStep.ADDRESS:
                return accountPrismicData.contact_details_heading[0].text;
            case BookingStep.VERIFICATION_CODE:
                return registerPrismicData.verification_code_input_label;
            case BookingStep.CONFIRM_APPOINTMENT:
                return isChainOverview
                    ? t('ChainOverviewTitle')
                    : bookAppointmentPrismicData.booking_summary.summary_heading[0].text;
            case BookingStep.CHAIN_SUGGESTIONS:
                return !isChainSuggestionFetching && !chainHasSuggestions
                    ? t('ChainNoAvailabilityTitle')
                    : t('ChainSuggestionsTitle');
            case BookingStep.INVALID_TIMESLOT:
                return bookAppointmentPrismicData.invalid_timeslot_title[0].text;
            default:
                return '';
        }
    };

    const getButtonText = () => {
        switch (currentStep) {
            case BookingStep.CONSENT:
                return bookAppointmentPrismicData.your_selection_cancel_button[0].text;
            case BookingStep.TIMESLOT_SELECTION:
                if (consentTreatments.length > 0) {
                    return bookAppointmentPrismicData.go_back_button[0].text;
                }
                return bookAppointmentPrismicData.your_selection_cancel_button[0].text;
            default:
                return bookAppointmentPrismicData.go_back_button[0].text;
        }
    };

    const getButtonTestId = () => {
        switch (currentStep) {
            case BookingStep.CONSENT:
                return 'booking-cancel-btn';
            case BookingStep.TIMESLOT_SELECTION:
                if (consentTreatments.length > 0) {
                    return 'booking-back-btn';
                }
                return 'booking-cancel-btn';
            default:
                return 'booking-back-btn';
        }
    };

    const checkDisabledState = () => {
        switch (currentStep) {
            case BookingStep.TIMESLOT_SELECTION:
                return !selectedTimeslot;
            case BookingStep.REGISTRATION:
                return (
                    !selectedGender ||
                    !dob ||
                    !firstName ||
                    !lastName ||
                    !email ||
                    !isEmail(email) ||
                    !phoneNumber ||
                    !isMobilePhoneNumber(phoneNumber) ||
                    !password ||
                    !isStrongPassword(password, {
                        minLength: 8,
                        minNumbers: 1,
                        minSymbols: 1,
                        minUppercase: 0,
                        minLowercase: 0,
                    }) ||
                    !confirmPassword ||
                    password !== confirmPassword ||
                    !isConsentConfirmed
                );
            case BookingStep.ADDRESS: {
                // The selector and the payload both fall back to DEFAULT_COUNTRY, so validating the raw
                // value would leave Continue disabled on a step that visibly shows a country.
                const effectiveCountry = country || DEFAULT_COUNTRY;
                return (
                    !street ||
                    !houseNumber ||
                    !city ||
                    !effectiveCountry ||
                    !zipCode ||
                    !isValidPostalCode(zipCode, effectiveCountry)
                );
            }
            case BookingStep.VERIFICATION_CODE:
                return !verificationCode || verificationCode.length !== 6;
            case BookingStep.CONSENT:
                return !consentToTreatments;
            default:
                return false;
        }
    };

    const getPracticeAddress = () => locations[selectedLocation]?.address[0]?.text;

    const getGoogleMapsUrl = () => locations[selectedLocation]?.google_maps?.url;

    useEffect(() => {
        modalRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, [currentStep]);

    const Component = StepComponent[currentStep];

    const headerTitle = showProgressBar
        ? `${registrationSteps.indexOf(currentStep) + 1}/${registrationSteps.length} ${getStepTitle()}`
        : getStepTitle();

    return (
        <div className={classes.container}>
            <div className={clsx(classes.modalWrapper, showLoginModal && classes.hiddenBehindLogin)}>
                <form className={classes.modal} onSubmit={(event) => event.preventDefault()} noValidate>
                    <div className={classes.header}>
                        <Typography className={classes.stepTitle}>{headerTitle}</Typography>
                        <IconButton
                            onClick={() => useBookingStore.setState({ showBookingModal: false })}
                            className={classes.headerClose}
                            data-testid="booking-close-btn"
                        >
                            <img src={closeIcon} alt="close" />
                        </IconButton>
                    </div>
                    <div className={classes.body} ref={modalRef}>
                        {currentStep === BookingStep.TIMESLOT_SELECTION && (
                            <Typography className={classes.stepSubtitle}>
                                {Object.keys(selectedTreatments)
                                    .filter((treatmentKey) => selectedTreatments[treatmentKey])
                                    .map((treatmentKey) => treatments[selectedLocation]?.[treatmentKey].name[0].text)
                                    .join(', ')}
                                {' · '}
                                {isOnlineAppointment && 'Online'}
                                {!isOnlineAppointment &&
                                    (getGoogleMapsUrl() ? (
                                        <a
                                            className={classes.stepSubtitleLink}
                                            href={getGoogleMapsUrl()}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {getPracticeAddress()}
                                        </a>
                                    ) : (
                                        getPracticeAddress()
                                    ))}
                            </Typography>
                        )}
                        {Component && <Component consent={consentToTreatments} setConsent={setConsentToTreatments} />}
                    </div>
                    {currentStep !== BookingStep.INVALID_TIMESLOT && (
                        <div className={classes.navBar}>
                            <UnderlinedButton
                                onPress={previousStep}
                                noMarginTop
                                text={getButtonText()}
                                disabled={
                                    loading ||
                                    (currentStep === BookingStep.CHAIN_SUGGESTIONS && isChainSuggestionFetching)
                                }
                                dataTestId={getButtonTestId()}
                            />
                            {currentStep !== BookingStep.AUTH_SELECTION &&
                                (currentStep !== BookingStep.TIMESLOT_SELECTION ||
                                    professionalDetails?.is_bookable_by_patient !== false) &&
                                // No Continue in the chain empty/fallback state — there's no suggestion to advance with.
                                (currentStep !== BookingStep.CHAIN_SUGGESTIONS || chainHasSuggestions) && (
                                    <FilledButton
                                        disabled={
                                            currentStep === BookingStep.CHAIN_SUGGESTIONS
                                                ? isChainContinueDisabled({
                                                      isChainSuggestionFetching: isChainSuggestionFetching,
                                                  })
                                                : checkDisabledState() || loading
                                        }
                                        onPress={nextStep}
                                        text={bookAppointmentPrismicData.continue_button[0].text}
                                        fullWidth={matchesXs}
                                        dataTestId="booking-continue-btn"
                                    />
                                )}
                        </div>
                    )}
                </form>
            </div>
            <LoginDialogBox
                openPopUp={showLoginModal}
                onClosePopUp={() => useBookingStore.setState({ showLoginModal: false })}
                onLoginSuccess={() => {
                    if (isChainFlow) {
                        useBookingStore.setState({ showLoginModal: false });
                        goToChainOverview();
                        return;
                    }
                    useBookingStore.setState({ showLoginModal: false, currentStep: BookingStep.CONFIRM_APPOINTMENT });
                }}
            />
        </div>
    );
};

export default BookingModal;
